import React, { useEffect, useState } from "react";
import { Plus, Trash2, Send, Mail, CheckCircle2 } from "lucide-react";
import { Button, Field, ErrorMessage, Modal } from "./components.jsx";
import { api } from "./api.js";

function deadlineDay(iso) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Boise",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(new Date(iso).getTime() - 1));
  return ["year", "month", "day"]
    .map((t) => parts.find((p) => p.type === t).value)
    .join("-");
}
function mountainCutoff(day) {
  const zone = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Boise",
    timeZoneName: "shortOffset",
  })
    .formatToParts(new Date(`${day}T23:00:00Z`))
    .find((p) => p.type === "timeZoneName").value;
  const offset = Number(zone.replace("GMT", ""));
  return new Date(
    new Date(
      `${day}T23:59:59.999${offset < 0 ? "-" : "+"}${String(Math.abs(offset)).padStart(2, "0")}:00`,
    ).getTime() + 1,
  ).toISOString();
}

export function WorkshopSettings({ settings, emailEnabled, onSave }) {
  const [value, setValue] = useState(settings),
    [day, setDay] = useState(deadlineDay(settings.deadline)),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const { settings } = await api("/api/admin/settings", {
        method: "PUT",
        body: { ...value, deadline: mountainCutoff(day) },
      });
      onSave(settings);
      setValue(settings);
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  function locationField(i, key, v) {
    setValue({
      ...value,
      locations: value.locations.map((l, j) =>
        j === i ? { ...l, [key]: v } : l,
      ),
    });
    setSaved(false);
  }
  return (
    <form className="settings-form" onSubmit={submit}>
      <section className="settings-card">
        <div className="settings-heading">
          <div>
            <span className="eyebrow">THE SEASON’S FRONT DOOR</span>
            <h2>Are we taking orders?</h2>
            <p>
              Close early when your workshop is full. The deadline also closes
              checkout automatically.
            </p>
          </div>
          <button
            type="button"
            className={`toggle ${value.ordersOpen ? "on" : ""}`}
            role="switch"
            aria-checked={value.ordersOpen}
            aria-label="Accept new orders"
            onClick={() => {
              setValue({ ...value, ordersOpen: !value.ordersOpen });
              setSaved(false);
            }}
          >
            <span />
          </button>
        </div>
        <p className="setting-status">
          {value.ordersOpen
            ? "Ordering switch is on."
            : "Ordering switch is off."}{" "}
          Save below to apply changes.
        </p>
        <Field label="Last day to order & drop letters (Mountain time)">
          <input
            type="date"
            required
            value={day}
            onChange={(e) => {
              setDay(e.target.value);
              setSaved(false);
            }}
          />
        </Field>
        <div className="closed-message-preview">
          We’re full for the season—thank you for making it magical!
        </div>
        <p className="fine-print">
          This message appears on the website when checkout is closed. Existing
          orders stay in the workshop.
        </p>
      </section>
      <section className="settings-card">
        <span className="eyebrow">A LITTLE LOCAL HELP</span>
        <h2>Letter & cash collection points</h2>
        <p>
          These locations appear on the home page, at checkout and in new order
          confirmations.
        </p>
        {value.locations.map((l, i) => (
          <fieldset className="location-editor" key={i}>
            <legend>Collection point {i + 1}</legend>
            <Field label="Business or location name">
              <input
                required
                maxLength={100}
                value={l.name}
                onChange={(e) => locationField(i, "name", e.target.value)}
              />
            </Field>
            <Field label="Full address">
              <input
                required
                maxLength={200}
                value={l.address}
                onChange={(e) => locationField(i, "address", e.target.value)}
              />
            </Field>
            <Field label="Collection hours">
              <input
                required
                maxLength={160}
                value={l.hours}
                onChange={(e) => locationField(i, "hours", e.target.value)}
                placeholder="Monday–Friday, 9 am–5 pm"
              />
            </Field>
            <button
              type="button"
              className="text-link danger-link"
              onClick={() => {
                setValue({
                  ...value,
                  locations: value.locations.filter((_, j) => j !== i),
                });
                setSaved(false);
              }}
            >
              <Trash2 size={13} /> Remove collection point
            </button>
          </fieldset>
        ))}
        {!value.locations.length && (
          <div className="inline-notice">
            No collection points are published yet. The website clearly says
            locations are coming soon.
          </div>
        )}
        <Button
          type="button"
          className="button-outline button-small"
          onClick={() => {
            setValue({
              ...value,
              locations: [
                ...value.locations,
                { name: "", address: "", hours: "" },
              ],
            });
            setSaved(false);
          }}
          disabled={value.locations.length >= 20}
        >
          <Plus size={15} /> Add a collection point
        </Button>
      </section>
      <section className="settings-card">
        <span className="eyebrow">KEEPING IN TOUCH</span>
        <h2>Customer email</h2>
        <Field label="Public contact email">
          <input
            type="email"
            required
            maxLength={254}
            value={value.contactEmail}
            onChange={(e) => {
              setValue({ ...value, contactEmail: e.target.value });
              setSaved(false);
            }}
          />
        </Field>
        <div className={`email-mode ${emailEnabled ? "connected" : ""}`}>
          <Mail size={20} />
          <div>
            <strong>
              {emailEnabled
                ? "Email delivery is connected"
                : "Email preview mode"}
            </strong>
            <p>
              {emailEnabled
                ? "Order confirmations, full-payment receipts and delivered messages will be sent through SMTP."
                : "Messages are saved in the email outbox. Connect an SMTP account on the server before sending real customer emails."}
            </p>
          </div>
        </div>
      </section>
      <ErrorMessage>{error}</ErrorMessage>
      {saved && (
        <p className="success-message" role="status">
          <CheckCircle2 size={16} /> Season settings saved.
        </p>
      )}
      <Button disabled={busy}>
        {busy ? "Saving the little details…" : "Save season settings"}
        <CheckCircle2 size={16} />
      </Button>
    </form>
  );
}

export function WorkshopMail() {
  const [messages, setMessages] = useState(null),
    [enabled, setEnabled] = useState(false),
    [error, setError] = useState(""),
    [selected, setSelected] = useState(null),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    api("/api/admin/messages")
      .then((r) => {
        setMessages(r.messages);
        setEnabled(r.emailEnabled);
      })
      .catch((e) => setError(e.message));
  }, []);
  async function send(message) {
    setBusy(true);
    setError("");
    try {
      const r = await api(`/api/admin/messages/${message.id}/send`, {
        method: "POST",
        body: {},
      });
      setMessages(r.messages);
      setSelected(r.messages.find((m) => m.id === message.id));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <div className={`email-mode ${enabled ? "connected" : ""}`}>
        <Mail size={24} />
        <div>
          <strong>
            {enabled
              ? "Customer email is connected"
              : "Email preview mode — nothing is sent externally"}
          </strong>
          <p>
            {enabled
              ? "New order confirmations, payment receipts and delivered emails are sent automatically. Scheduling a route can also send families their delivery day. Retry a failed or older queued message below."
              : "Every message is saved here so you can review the complete order workflow before connecting an email account."}
          </p>
        </div>
      </div>
      <ErrorMessage>{error}</ErrorMessage>
      <section className="orders-panel email-list">
        <div className="orders-toolbar">
          <h2>The Christmas post</h2>
          <span>{messages?.length || 0} messages</span>
        </div>
        {messages?.map((m) => (
          <button
            className="email-row"
            key={m.id}
            onClick={() => setSelected(m)}
          >
            <span className="email-icon">
              <Mail size={20} />
            </span>
            <div>
              <strong>{m.subject}</strong>
              <span>{m.recipient}</span>
            </div>
            <span
              className={`badge ${m.status === "sent" ? "badge-paid" : m.status === "failed" ? "badge-cash" : "badge-new"}`}
            >
              {m.status === "queued" ? "In outbox" : m.status}
            </span>
            <span className="email-date">
              {new Date(m.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
            </span>
          </button>
        ))}
        {messages?.length === 0 && (
          <div className="empty-state">
            <Mail size={35} strokeWidth={1} />
            <h3>A little quiet at the post office.</h3>
            <p>Your first order confirmation will appear here.</p>
          </div>
        )}
      </section>
      {selected && (
        <Modal
          title="A little note from the workshop"
          onClose={() => setSelected(null)}
          wide
        >
          <div className="email-preview">
            <span className="eyebrow">TO {selected.recipient}</span>
            <h3>{selected.subject}</h3>
            <pre>{selected.body}</pre>
            {selected.error && <ErrorMessage>{selected.error}</ErrorMessage>}
            {enabled && selected.status !== "sent" && (
              <Button disabled={busy} onClick={() => send(selected)}>
                <Send size={15} />
                {busy ? "Sending…" : "Send this email"}
              </Button>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
